/**
 * Checkout Page - Dedicated page for mobile checkout
 * Access at: /checkout-channel-invite
 * 
 * Full-screen Stripe checkout experience optimized for mobile
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import {
  STRIPE_PUBLISHABLE_KEY,
  STRIPE_APPEARANCE,
  STRIPE_APPEARANCE_EXCLUSIVE,
  CREATE_PAYMENT_INTENT_ENDPOINT,
  getPackPricing as getPackPricingStatic,
} from "@/config/stripe";
import { getPostPaymentSuccessPath } from "@/lib/muchasGraciasSuccessUrl";
import { usePackConfig } from "@/lib/usePackConfig";
import { Loader2 } from "lucide-react";

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get parameters from URL
  const packId = searchParams.get("pack") || "channel_invite";
  const countryCode = searchParams.get("country") || "US";
  const language = (searchParams.get("lang") || "es") as "en" | "es";
  const isExclusiveMembership = packId === "exclusive";
  
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  /** Alineado con respuesta de `/api/create-payment-intent` (descuento aleatorio channel_invite). */
  const [stripeChargeDetails, setStripeChargeDetails] = useState<{
    chargedAmountCents: number;
    originalAmountCents: number;
    viewerDiscountPercent: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { getPackPricing } = usePackConfig();
  // Para sorpresa/exclusive asegurar precio desde static y así siempre generar el custom element
  const packPricing = (["sorpresa", "exclusive"].includes(packId) ? getPackPricingStatic(packId, countryCode) : null) ?? getPackPricing(packId, countryCode);

  const createPaymentIntent = async () => {
    if (!packPricing) return;
    setError(null);
    setStripeChargeDetails(null);
    setIsLoading(true);
    try {
      const apiUrl = typeof window !== "undefined" ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}` : CREATE_PAYMENT_INTENT_ENDPOINT;
      // Custom Stripe: always USD regardless of country; amount is in USD cents
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: packPricing.amount,
          currency: "usd",
          packId: packId,
          source: window.location.pathname,
          country: countryCode || undefined,
        }),
      });
      const text = await response.text();
      if (!response.ok) {
        let errMsg = "Failed to create payment intent";
        try {
          const errorData = JSON.parse(text) as { error?: string; message?: string; code?: string; redirect_url?: string };
          
          errMsg = errorData.error || errorData.message || errMsg;
        } catch {
          if (response.status === 404) errMsg = language === "es" ? "No se encontró el servicio de pago." : "Payment service not found.";
          else if (response.status >= 500) errMsg = language === "es" ? "Error del servidor. Intenta de nuevo." : "Server error. Please try again.";
        }
        throw new Error(errMsg);
      }
      const data = JSON.parse(text) as {
        clientSecret?: string;
        viewerDiscountPercent?: number;
        originalAmountCents?: number;
        chargedAmountCents?: number;
      };
      if (!data.clientSecret) {
        throw new Error(language === "es" ? "Respuesta inválida del servidor" : "Invalid server response");
      }
      if (
        typeof data.viewerDiscountPercent === "number" &&
        typeof data.originalAmountCents === "number" &&
        typeof data.chargedAmountCents === "number"
      ) {
        setStripeChargeDetails({
          viewerDiscountPercent: data.viewerDiscountPercent,
          originalAmountCents: data.originalAmountCents,
          chargedAmountCents: data.chargedAmountCents,
        });
      } else {
        setStripeChargeDetails(null);
      }
      setClientSecret(data.clientSecret);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to initialize checkout");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) {
      setError("Stripe not configured");
      setIsLoading(false);
      return;
    }
    if (!packPricing) {
      setError(language === "es" ? "Producto no válido" : "Invalid product");
      setIsLoading(false);
      return;
    }
    createPaymentIntent();
  }, [packId, countryCode]);
  
  const handleSuccess = (paymentIntentId?: string, paymentIntentSecret?: string) => {
    // Build redirect URL with payment_intent parameters
    const params = new URLSearchParams();
    if (paymentIntentId) {
      params.append("payment_intent", paymentIntentId);
    }
    if (paymentIntentSecret) {
      params.append("payment_intent_client_secret", paymentIntentSecret);
    }

    // Membership flow: todo usuario que adquiera Exclusive Membership va al
    // form de perfil (Clerk + perfil → pending-approval → admin envía el link
    // privado por DM). EN aterriza en `/portal/complete-profile`; ES en
    // `/portal/completar-perfil`.
    if (isExclusiveMembership) {
      params.append("plan", "exclusiva");
      params.append("source", "checkout_page_exclusive");
      const postPaymentPath = getPostPaymentSuccessPath(language);
      const join = postPaymentPath.includes("?") ? "&" : "?";
      window.location.href = `${postPaymentPath}${join}${params.toString()}`;
      return;
    }

    // Resto de packs: gracias / thank-you por idioma.
    const thankYouPath = language === "es" ? "/gracias" : "/thank-you";
    window.location.href = `${thankYouPath}?${params.toString()}`;
  };
  
  const handleError = (error: string) => {
    setError(error);
  };
  
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  // Loading state
  if (isLoading) {
    const loadingText = language === "es" ? "Preparando checkout..." : "Preparing checkout...";
    return (
      <div
        className="min-h-screen bg-black flex items-center justify-center p-4 font-body"
        aria-busy="true"
        aria-live="polite"
        aria-label={loadingText}
      >
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" aria-hidden />
          <p className="text-white/60 text-sm font-medium">{loadingText}</p>
        </div>
      </div>
    );
  }

  // Error state (with retry for better UX)
  if (error || !packPricing || !clientSecret) {
    const errorTitle = language === "es" ? "Error en el pago" : "Payment error";
    const errorMsg = error || (language === "es" ? "No se pudo inicializar el checkout" : "Could not initialize checkout");
    const retryLabel = language === "es" ? "Reintentar" : "Try again";
    const backLabel = language === "es" ? "Volver" : "Go back";
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 font-body" role="alert">
        <div className="max-w-md w-full bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center space-y-4">
          <div className="text-red-400 text-4xl mb-2" aria-hidden>⚠️</div>
          <h2 className="text-xl font-bold text-white font-display">{errorTitle}</h2>
          <p className="text-white/60 text-sm font-medium">{errorMsg}</p>
          <div className="flex flex-col gap-3 pt-2">
            {packPricing && (
              <button
                type="button"
                onClick={createPaymentIntent}
                className="w-full bg-white text-black font-bold py-3 px-6 rounded-xl hover:bg-white/90 active:bg-white/95 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              >
                {retryLabel}
              </button>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="w-full bg-white/10 text-white font-bold py-3 px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Checkout form
  const stripePromise = getStripe();
  if (!stripePromise) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/60">Stripe not configured</p>
      </div>
    );
  }
  
  return (
    // h-dvh (dynamic viewport height) handles iOS in-app browsers correctly:
    // unlike min-h-screen/100vh which ignores the address bar, dvh adjusts
    // to the actual visible area so the Pay button is never below the fold.
    <div 
      className="h-dvh flex flex-col bg-black font-body"
      style={{
        // Safari iOS: Prevent bounce scrolling on main container
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        // Android: Ensure proper rendering
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: isExclusiveMembership
            ? STRIPE_APPEARANCE_EXCLUSIVE
            : STRIPE_APPEARANCE,
          locale: language === "es" ? "es" : "en",
        }}
      >
        {/* Elements is a context provider with no DOM node — this div
            gives StripeCheckoutForm an explicit flex-1 height to inherit */}
        <div className="flex-1 flex flex-col min-h-0">
          <StripeCheckoutForm
            amount={
              stripeChargeDetails?.chargedAmountCents ?? packPricing.amount
            }
            currency="usd"
            packName={language === "es" ? packPricing.nameEs : packPricing.name}
            packThumbnail={packPricing.thumbnail}
            originalAmount={stripeChargeDetails?.originalAmountCents}
            discountPercent={stripeChargeDetails?.viewerDiscountPercent}
            language={language}
            variant={isExclusiveMembership ? "exclusive" : "default"}
            returnUrl={
              isExclusiveMembership && typeof window !== "undefined"
                ? (() => {
                    const path = getPostPaymentSuccessPath(language);
                    const join = path.includes("?") ? "&" : "?";
                    return `${window.location.origin}${path}${join}plan=exclusiva&source=checkout_page_exclusive_3ds`;
                  })()
                : undefined
            }
            onSuccess={handleSuccess}
            onError={handleError}
            onBack={handleBack}
          />
        </div>
      </Elements>
    </div>
  );
}
